import React, { useCallback } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Card, message } from 'antd';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { updateAccount } from '../../../../stores/accounts';
import { useUpdateAmazonProductProviderMutation } from '../../../../stores/productProvidersSlice';

// Constants
const ACCOUNT_TYPES = {
  SELLER: 'SELLER',
  VENDOR: 'VENDOR',
} as const;

const CARD_STYLES = {
  border: '1px solid #d9d9d9',
  borderRadius: '10px',
  marginBottom: '20px',
};

const BUTTON_STYLES = {
  primary: {
    backgroundColor: '#4d4dff',
    borderColor: '#4d4dff',
  },
  secondary: {
    backgroundColor: '#4d4dff',
    borderColor: '#4d4dff',
    color: 'white',
  },
};

// Styled Components
const ContentContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const IconContainer = styled.div`
  color: #d93f21;
  font-size: 18px;
  flex-shrink: 0;
`;

const TextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Description = styled.p`
  margin: 0;
  color: #666;
  line-height: 1.5;
  letter-spacing: 0.5px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
`;

interface ProductProvider {
  id: string;
  accountType: string;
}

// Types
interface SPAPIConnectionNoticeProps {
  account: { id: string; productProviders: ProductProvider[] };
  productProvider: ProductProvider;
  onConnectSPAPI: () => void;
  onSkipSPAPI: () => void;
}

export interface SPAPIConnectionNoticeMessages {
  sellerAccount: {
    title: string;
    description: string;
  };
  vendorAccount: {
    title: string;
    description: string;
    linkText: string;
  };
  defaultAccount: {
    title: string;
    description: string;
    connectButton: string;
    skipButton: string;
  };
  successMessage: string;
  errorMessage: string;
}

// Component
export const SPAPIConnectionNotice: React.FC<SPAPIConnectionNoticeProps> = ({
  account,
  productProvider,
  onConnectSPAPI,
  onSkipSPAPI,
}) => {
  const history = useHistory();
  const { messages } = useIntl();
  const spapiMessages = messages.spapiConnectionNotice as unknown as SPAPIConnectionNoticeMessages;
  const [updateAmazonProductProvider, { isLoading }] = useUpdateAmazonProductProviderMutation();
  const dispatch = useDispatch();

  const connectSPAPIUrl = `/${account.id}/amazon/account`;

  const handleConnectSPAPI = useCallback(async () => {
    await history.push(connectSPAPIUrl);
    onConnectSPAPI();
  }, [connectSPAPIUrl, history, onConnectSPAPI]);

  const handleSkipSPAPI = useCallback(async () => {
    try {
      const result = await updateAmazonProductProvider({
        productProviderId: productProvider.id,
        accountId: account.id,
        data: { accountType: ACCOUNT_TYPES.VENDOR },
      });

      if (result.error) {
        throw new Error('Failed to update account type');
      }

      message.success(spapiMessages.successMessage);
      const { provider } = result.data;

      if (provider) {
        const updatedAccount = {
          ...account,
          productProviders: account.productProviders.map((pp) => (pp.id === productProvider.id ? provider : pp)),
        };

        // update store with updated account
        dispatch(updateAccount(updatedAccount));
      }

      onSkipSPAPI();
    } catch {
      message.error(spapiMessages.errorMessage);
    }
  }, [updateAmazonProductProvider, productProvider?.id, account.id, onSkipSPAPI, spapiMessages]);

  if (productProvider?.accountType === ACCOUNT_TYPES.SELLER) {
    return (
      <Card style={CARD_STYLES}>
        <ContentContainer>
          <IconContainer>
            <InfoCircleOutlined style={{ color: '#4d4dff' }} />
          </IconContainer>
          <TextContainer>
            <Title>{spapiMessages.sellerAccount.title}</Title>
            <Description>{spapiMessages.sellerAccount.description}</Description>
          </TextContainer>
        </ContentContainer>
      </Card>
    );
  }

  if (productProvider?.accountType === ACCOUNT_TYPES.VENDOR) {
    return (
      <Card style={CARD_STYLES}>
        <ContentContainer>
          <IconContainer>
            <InfoCircleOutlined style={{ color: '#4d4dff' }} />
          </IconContainer>
          <TextContainer>
            <Title>{spapiMessages.vendorAccount.title}</Title>
            <Description>{spapiMessages.vendorAccount.description}</Description>
            <Description>
              <Button
                type="link"
                style={{ padding: 0, height: 'auto', fontSize: 'inherit', textDecoration: 'underline' }}
                onClick={() => history.push(connectSPAPIUrl)}
              >
                {spapiMessages.vendorAccount.linkText}
              </Button>
            </Description>
          </TextContainer>
        </ContentContainer>
      </Card>
    );
  }

  return (
    <Card style={CARD_STYLES}>
      <ContentContainer>
        <IconContainer>
          <InfoCircleOutlined />
        </IconContainer>
        <TextContainer>
          <Title>{spapiMessages.defaultAccount.title}</Title>
          <Description>{spapiMessages.defaultAccount.description}</Description>
          <ButtonContainer>
            <Button type="primary" style={BUTTON_STYLES.primary} onClick={handleConnectSPAPI}>
              {spapiMessages.defaultAccount.connectButton}
            </Button>
            <Button style={BUTTON_STYLES.secondary} onClick={handleSkipSPAPI} loading={isLoading}>
              {spapiMessages.defaultAccount.skipButton}
            </Button>
          </ButtonContainer>
        </TextContainer>
      </ContentContainer>
    </Card>
  );
};
